import { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';

import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { DealerProspect } from '../../../models/dealer-prospect.model';
import { archiveDealerProspect, getDealerProspects, unarchiveDealerProspect } from '../../../services/dealer-prospects.service';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import ProspectsPageBar from '../../../components/admin/prospects-page-bar/prospects-page-bar.component';
import ProspectsTable from '../../../components/admin/prospects-table/prospects-table.component';
import { hasPermission, Permission } from '../../../models/role.model';
import { AwsCognitoUser } from '../../../models/user.model';
import { CompanyConfig } from '../../../company.config';

const LIMIT = 100;

/** Slugify for URL path: lowercase, spaces to hyphens, keep alphanumeric and hyphens */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Build the shareable prospect form URL for a dealer/prospect */
function buildProspectFormUrl(prospectName: string, dealerName: string): string {
  const base = CompanyConfig.leadFormBaseUrl;
  const prospectSlug = slugify(prospectName) || 'prospect';
  const dealerSlug = slugify(dealerName) || 'dealer';
  return `${base}/prospect/name/${prospectSlug}/dealer/${dealerSlug}`;
}

function showArchiveButton(user: AwsCognitoUser) {
  return hasPermission(user, Permission.ARCHIVE_DEALER_PROSPECT);
}

function showAddDealerProspectButton(user: AwsCognitoUser) {
  return hasPermission(user, Permission.CREATE_DEALER_PROSPECT);
}

export default function DealerProspectsContainer() {
  const { isAuthenticated, currentUser } = useAppContext();

  const [leads, setLeads] = useState<DealerProspect[]>([]);
  const [lastKey, setLastKey] = useState<string>('');
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [currentItemId, setCurrentItemId] = useState<string>('');
  const [showArchivedLeads, setShowArchivedLeads] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySnackbarOpen(true);
    } catch (e) {
      onError(e);
    }
  };

  const handleClickOpenArchive = (itemId: string) => {
    setCurrentItemId(itemId);
    setShowArchiveModal(true);
  };

  const handleClickOpenUnarchive = (itemId: string) => {
    setCurrentItemId(itemId);
    setShowUnarchiveModal(true);
  };

  const handleCloseArchive = () => setShowArchiveModal(false);
  const handleCloseUnarchive = () => setShowUnarchiveModal(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await archiveDealerProspect(currentItemId);
    } catch (err) {
      alert(err);
    } finally {
      setShowArchiveModal(false);
      setIsArchiving(false);
      onLoad();
    }
  };

  const handleUnarchive = async () => {
    setIsUnarchiving(true);
    try {
      await unarchiveDealerProspect(currentItemId);
    } catch (err) {
      alert(err);
    } finally {
      setShowUnarchiveModal(false);
      setIsUnarchiving(false);
      onLoad();
    }
  };

  async function onLoad(name = '', cursor = '') {
    if (!isAuthenticated) {
      return;
    }

    setLeads([]);
    setIsLoading(true);
    try {
      const response = await getDealerProspects({ name, archived: showArchivedLeads, lastKey: cursor, limit: LIMIT });
      setLeads(response.items);
      setLastKey(response.lastKey);
    } catch (e) {
      onError(e);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUser]);

  const handleSearch = (search: string) => {
    onLoad(search);
  };

  useEffect(() => {
    if (shouldReload) {
      onLoad();
      setShouldReload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReload]);

  const handleSearchClear = () => {
    setShowArchivedLeads(false);
    setShouldReload(true);
  };

  function renderDealerProspects() {
    return (
      <ProspectsPageBar
        title="Dealer Prospects"
        createButton={
          currentUser && showAddDealerProspectButton(currentUser) ? (
            <ReactRouterLink to="/dealer-prospects/new">
              <Button variant="contained">New Prospect</Button>
            </ReactRouterLink>
          ) : null
        }
        onSearch={handleSearch}
        onSearchClear={handleSearchClear}
      >
        <FormControlLabel
          control={<Checkbox />}
          label="Archived"
          checked={showArchivedLeads}
          onChange={(_e) => setShowArchivedLeads(!showArchivedLeads)}
        />
      </ProspectsPageBar>
    );
  }

  const handleNext = () => {
    if (lastKey) {
      setCursorHistory([...cursorHistory, currentCursor]);
      setCurrentCursor(lastKey);
      onLoad('', lastKey);
    }
  };

  const handlePrev = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop() || '';
      setCursorHistory(newHistory);
      setCurrentCursor(previousCursor);
      onLoad('', previousCursor);
    }
  };

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <div className="DealerProspects">
          {renderDealerProspects()}
          {isLoading ? (
            <CircularProgressComponent />
          ) : (
            <ProspectsTable
              leads={leads}
              getCopyUrl={(row) => buildProspectFormUrl(row.leadTypeName, row.dealershipName)}
              onCopyLink={handleCopyLink}
              showArchiveActions
              canArchive={showArchiveButton}
              currentUser={currentUser}
              onArchive={handleClickOpenArchive}
              onUnarchive={handleClickOpenUnarchive}
            />
          )}
          {!isLoading && leads.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 10 }}>
              <Button onClick={handlePrev} disabled={cursorHistory.length === 0}>
                Previous
              </Button>
              <Button onClick={handleNext} disabled={!lastKey}>
                Next
              </Button>
            </Box>
          )}
        </div>
      </Grid>

      <Dialog
        open={showArchiveModal}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          handleCloseArchive();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to archive this prospect?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchive} disabled={isArchiving}>
            Archive
          </Button>
          <Button onClick={handleCloseArchive} disabled={isArchiving} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUnarchiveModal}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          handleCloseUnarchive();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to unarchive this prospect?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnarchive} disabled={isUnarchiving}>
            Unarchive
          </Button>
          <Button onClick={handleCloseUnarchive} disabled={isUnarchiving} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
